import { useState, useEffect, useRef } from "react";
import {
  Building2,
  Home,
  Briefcase,
  GraduationCap,
  ShoppingBag,
  Building,
} from "lucide-react";

// 3D Building Icons for slide content
import residentialIcon from "../../client/icons/residential_m3d.png";
import mallIcon from "../../client/icons/mall_m3d.png";
import universityIcon from "../../client/icons/university_m3d.png";

interface UseCase {
  id: string;
  name: string;
  tabIcon: React.ReactNode;
  slideIcon: string | null;
  headline: string;
  description: string;
  features: string[];
  gradient: string;
}

const useCases: UseCase[] = [
  {
    id: "buildings",
    name: "Buildings & Facilities",
    tabIcon: <Building2 className="w-5 h-5" />,
    slideIcon: universityIcon,
    headline: "Explore your campus",
    description:
      "From state-of-the-art laboratories to historic lecture halls. Access real-time occupancy, floor plans, and navigation for every building.",
    features: [
      "Building directory",
      "Interactive maps",
      "Room availability",
      "Facility reporting",
    ],
    gradient: "from-blue-500/20 via-indigo-500/10 to-purple-500/20",
  },
  {
    id: "student-life",
    name: "Student Life",
    tabIcon: <GraduationCap className="w-5 h-5" />,
    slideIcon: null,
    headline: "Thrive beyond classroom",
    description:
      "Join student organizations, discover campus events, and connect with your peers. Your hub for everything social and extracurricular.",
    features: [
      "Club portal",
      "Event calendar",
      "Social forums",
      "Student discounts",
    ],
    gradient: "from-green-500/20 via-emerald-500/10 to-teal-500/20",
  },
  {
    id: "academics",
    name: "Academics",
    tabIcon: <Briefcase className="w-5 h-5" />, // Changed from Briefcase to something else if needed, but let's keep it consistent for now
    slideIcon: null,
    headline: "Excel in your studies",
    description:
      "Access academic support, research opportunities, and study group finders. Everything you need to succeed in your degree.",
    features: [
      "Study groups",
      "Research labs",
      "Academic support",
      "Library resources",
    ],
    gradient: "from-orange-500/20 via-amber-500/10 to-yellow-500/20",
  },
  {
    id: "campus",
    name: "Campus Services",
    tabIcon: <Home className="w-5 h-5" />,
    slideIcon: residentialIcon,
    headline: "Essential student services",
    description:
      "One-stop shop for housing, dining, health services, and career guidance. We handle the logistics so you can focus on learning.",
    features: [
      "Housing portal",
      "Dining menus",
      "Health & wellness",
      "Career services",
    ],
    gradient: "from-red-500/20 via-rose-500/10 to-pink-500/20",
  },
];

export default function UseCases() {
  const [activeCase, setActiveCase] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const currentCase = useCases[activeCase];

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">
            University Life
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Everything you need to thrive
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            From exploring historic buildings to joining your first student club,
            Gedoba is your digital guide to every corner of university life.
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {useCases.map((useCase, index) => (
            <button
              key={useCase.id}
              onClick={() => setActiveCase(index)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeCase === index
                ? "bg-primary !text-white shadow-lg"
                : "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
                }`}
            >
              {useCase.tabIcon}
              <span className="hidden sm:inline">{useCase.name}</span>
            </button>
          ))}
        </div>

        {/* Content area */}
        <div
          className={`relative rounded-3xl overflow-hidden transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          {/* Gradient background */}
          <div
            className={`absolute inset-0 bg-gradient-to-br ${currentCase.gradient} transition-all duration-500`}
          />

          {/* Content grid */}
          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-16 p-8 lg:p-16">
            {/* Left - Text content */}
            <div className="flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 text-primary mb-4">
                {currentCase.tabIcon}
                <span className="text-sm font-semibold uppercase tracking-wide">
                  {currentCase.name}
                </span>
              </div>

              <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                {currentCase.headline}
              </h3>

              <p className="text-muted-foreground text-lg mb-8">
                {currentCase.description}
              </p>

              {/* Features list */}
              <div className="grid grid-cols-2 gap-3">
                {currentCase.features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2 text-sm"
                  >
                    <svg
                      className="w-5 h-5 text-primary flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Visual representation */}
            <div className="flex items-center justify-center">
              <div className="relative w-full max-w-md aspect-square">
                {/* Decorative circles */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-full rounded-full border-2 border-primary/10 animate-pulse-glow" />
                </div>
                <div className="absolute inset-8 flex items-center justify-center">
                  <div className="w-full h-full rounded-full border-2 border-primary/20" />
                </div>
                <div className="absolute inset-16 flex items-center justify-center">
                  <div className="w-full h-full rounded-full border-2 border-primary/30" />
                </div>

                {/* Central icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {currentCase.slideIcon ? (
                    <img
                      src={currentCase.slideIcon}
                      alt={currentCase.name}
                      className="w-40 h-40 lg:w-48 lg:h-48 object-contain animate-float drop-shadow-xl"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-primary/10 backdrop-blur-sm flex items-center justify-center text-primary animate-float">
                      <div className="scale-150">{currentCase.tabIcon}</div>
                    </div>
                  )}
                </div>

                {/* Floating feature badges */}
                {currentCase.features.map((feature, index) => {
                  const positions = [
                    "top-0 left-1/2 -translate-x-1/2",
                    "bottom-0 left-1/2 -translate-x-1/2",
                    "left-0 top-1/2 -translate-y-1/2",
                    "right-0 top-1/2 -translate-y-1/2",
                  ];
                  return (
                    <div
                      key={feature}
                      className={`absolute ${positions[index]} px-3 py-1.5 rounded-full bg-card/80 backdrop-blur-sm text-xs font-medium shadow-lg animate-float`}
                      style={{ animationDelay: `${index * 0.5}s` }}
                    >
                      {feature}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
